// src/components/lesson/KeyTakeawayTemplate.jsx

import React, {
  useMemo,
} from "react";

import {
  useNavigate,
} from "react-router-dom";

import LessonShell from "./LessonShell";

const TEMP_SUMMARY_IMAGE =
  "https://placehold.co/520x520/F1F1F1/999999?text=Replace+Summary+Penguin";

function splitSummaryText(
  value
) {
  if (
    value == null ||
    React.isValidElement(
      value
    )
  ) {
    return [];
  }

  return String(value)
    .replace(
      /<br\s*\/?>/gi,
      "\n"
    )
    .split(/\n+/)
    .map((line) =>
      line.trim()
    )
    .filter(Boolean);
}

export default function KeyTakeawayTemplate({
  speechText = "",

  nextLabel = "Next",
  onNext,
  nextTo,

  backTo,
  backLabel = "Back",
  onBack,

  progressStep = 8,
  progressTotal = 11,

  /*
   * Canva summary
   */
  summaryImageSrc =
    TEMP_SUMMARY_IMAGE,

  summaryImageAlt =
    "Gwin mascot",

  heading =
    "Financial control starts by asking:",

  highlight =
    "What should I check first?",

  className = "",
}) {
  const navigate =
    useNavigate();

  const summaryLines =
    useMemo(
      () =>
        splitSummaryText(
          speechText
        ),
      [speechText]
    );

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }

    if (backTo) {
      navigate(
        backTo
      );
      return;
    }

    navigate(-1);
  };

  const handleNext = () => {
    if (onNext) {
      onNext();
      return;
    }

    if (nextTo) {
      navigate(
        nextTo
      );
    }
  };

  return (
    <LessonShell
      progressStep={
        progressStep
      }
      progressTotal={
        progressTotal
      }

      showBack
      backLabel={
        backLabel
      }
      onBack={
        handleBack
      }

      showNext
      nextLabel={
        nextLabel
      }
      onNext={
        handleNext
      }

      className={
        className
      }

      contentClassName="
        pt-8
      "
    >
      {/*
       * SUMMARY LABEL
       */}
      <p
        className="
          text-center
          text-[18px]
          font-extrabold
          uppercase
          text-[#FF914D]
        "
      >
        Summary
      </p>

      {/*
       * SUMMARY VISUAL
       */}
      <section
        className="
          mx-auto
          mt-14
          w-full
          max-w-[560px]
        "
      >
        <div
          className="
            relative
            min-h-[390px]
            overflow-hidden
            rounded-[26px]
            bg-gradient-to-b
            from-[#F3F3F3]
            to-[#EAEAEA]
            px-7
            pb-0
            pt-10
          "
        >
          <div
            className="
              relative
              z-10
              max-w-[380px]
            "
          >
            <p
              className="
                text-[18px]
                font-extrabold
                leading-[1.45]
                text-[#111111]
              "
            >
              {heading}
            </p>

            <p
              className="
                mt-1
                text-[18px]
                font-extrabold
                leading-[1.45]
                text-[#FF7A3D]
              "
            >
              “{highlight}”
            </p>
          </div>

          {!!summaryImageSrc && (
            <img
              src={
                summaryImageSrc
              }
              alt={
                summaryImageAlt
              }
              className="
                absolute
                bottom-0
                left-1/2
                z-10
                h-auto
                w-[58%]
                max-w-[285px]
                -translate-x-1/2
                object-contain
              "
              loading="eager"
              decoding="async"
              draggable="false"
            />
          )}
        </div>
      </section>

      {/*
       * Existing Sanity summary text remains
       * available underneath if present.
       *
       * This keeps content compatibility
       * without changing Sanity yet.
       */}
      {summaryLines.length >
        0 && (
        <section
          className="
            mx-auto
            mt-8
            w-full
            max-w-[560px]
            space-y-3
          "
        >
          {summaryLines.map(
            (
              line,
              index
            ) => (
              <p
                key={`${index}-${line}`}
                className="
                  text-center
                  text-[15px]
                  font-medium
                  leading-[1.6]
                  text-[#555555]
                "
              >
                {line}
              </p>
            )
          )}
        </section>
      )}
    </LessonShell>
  );
}